import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { activeChannelAtom } from '../../../atoms/activeChannelAtom';
import { messageAtom } from '../../../atoms/chat/messageAtom';
import ChatMessage from './ChatMessage';
import ChatSystemMessage from './ChatSystemMessage';
import { Box, Flex } from '@chakra-ui/react';
import useIsMobile from '../../../hooks/useIsMobile';
import { usePlayerField } from '../../../hooks/hooks';
import { FaArrowCircleDown } from 'react-icons/fa';
import badwords from 'bad-words';
import {
	IChatMessage,
	ISystemMessage,
	IWhisperMessage,
} from '../../../../../game-server/src/modules/chat/Chat.interface';

// Pre-compile regex patterns for better performance
const WORD_SPLITTER = /\s+/;
const CLEAN_REGEX = /[^a-zA-Z0-9]/g;
const REPEAT_CHAR_REGEX = /(.)\1+/g;
const MAX_CACHE_SIZE = 10000;
const unfilterWords = ['bestial', 'god', 'slag'];

// Create memoized components to prevent unnecessary re-renders
const MemoizedChatMessage = React.memo(ChatMessage);
const MemoizedChatSystemMessage = React.memo(ChatSystemMessage);

const ChatMessageList = () => {
	const channels = useRecoilValue(messageAtom);
	const activeChannel = useRecoilValue(activeChannelAtom);
	const container = useRef<HTMLDivElement>(null);
	const settings = usePlayerField('settings');
	const isMobile = useIsMobile();
	const [stickToBottom, setStickToBottom] = useState(true);

	// Create filter and cache only once with memoization
	const { filter, wordCache } = useMemo(() => {
		const f = new badwords();
		f.removeWords(...unfilterWords);

		// Create a size-limited cache
		const cache = new Map<string, string>();
		const accessOrder: string[] = [];

		const enhancedCache = {
			has: (key: string) => cache.has(key),
			get: (key: string) => {
				const value = cache.get(key);
				if (value !== undefined) {
					// Update access order for LRU tracking
					const index = accessOrder.indexOf(key);
					if (index > -1) {
						accessOrder.splice(index, 1);
					}
					accessOrder.push(key);
				}
				return value;
			},
			set: (key: string, value: string) => {
				if (cache.size >= MAX_CACHE_SIZE && !cache.has(key)) {
					// Remove least recently used item
					const oldest = accessOrder.shift();
					if (oldest) cache.delete(oldest);
				}

				cache.set(key, value);

				// Track access order
				const index = accessOrder.indexOf(key);
				if (index > -1) {
					accessOrder.splice(index, 1);
				}
				accessOrder.push(key);

				return value;
			},
		};

		return {
			filter: f,
			wordCache: enhancedCache,
		};
	}, []);

	const fontSize = settings.chat.fontSize;
	const channelKeys = Object.keys(channels);
	const activeChannelMessages = channels[channelKeys[activeChannel]];
	const shouldFilterProfanity = settings.chat.profanityFilter;

	// Optimized filter function with cache
	const filterMessage = useMemo(() => {
		return function (text: string): string {
			if (!text || !shouldFilterProfanity) return text;

			// Process chunks with cache
			return text
				.split(WORD_SPLITTER)
				.map((word) => {
					// Check cache first for exact matches
					if (wordCache.has(word)) {
						return wordCache.get(word)!;
					}

					// Quick check for profanity
					if (filter.isProfane(word)) {
						const cleaned = filter.clean(word);
						wordCache.set(word, cleaned);
						return cleaned;
					}

					// Handle complex cases with a single regex operation
					const cleanedWord = word.replace(CLEAN_REGEX, '').replace(REPEAT_CHAR_REGEX, '$1');
					if (cleanedWord && cleanedWord !== word && filter.isProfane(cleanedWord)) {
						const cleaned = filter.clean(word);
						wordCache.set(word, cleaned);
						return cleaned;
					}

					// Cache the result for non-profane words too
					wordCache.set(word, word);
					return word;
				})
				.join(' ');
		};
	}, [filter, wordCache, shouldFilterProfanity]);

	// Memoize processed messages to avoid reprocessing on re-renders
	const processedMessages = useMemo(() => {
		if (!activeChannelMessages || !shouldFilterProfanity) return activeChannelMessages;

		return activeChannelMessages.map((message) => {
			if ('type' in message && message.type === 'settings') {
				return message;
			}
			if ('type' in message && message.type === 'system-message') {
				return processSystemMessage(message);
			}
			return processChatMessage(message);
		});
	}, [activeChannelMessages, shouldFilterProfanity]);

	/** Check if the container should be scrolled on new message */
	useEffect(() => {
		checkScroll();
	}, [activeChannelMessages]);

	/** When changing tabs, always scroll to bottom */
	useEffect(() => {
		scrollToBottom();
	}, [activeChannel]);

	if (!activeChannelMessages)
		return (
			<Flex className='chat-message-list' justifyContent='center' alignItems='center'>
				Select a channel at the {isMobile ? 'top' : 'left'}.
			</Flex>
		);

	const activeChannelSettings = activeChannelMessages.length > 0 ? activeChannelMessages[0] : undefined;

	const channelName = activeChannelSettings?.channelName;
	if (channelName === 'Activity' && settings.chat.hideActivityLog) {
		return (
			<Flex className='chat-message-list' justifyContent='center' alignItems='center'>
				The Activity Log is hidden. Check your Settings to unhide it.
			</Flex>
		);
	} else if (channelName === 'Group Chat' && settings.chat.hideGroupChat) {
		return (
			<Flex className='chat-message-list' justifyContent='center' alignItems='center'>
				The Group Chat is hidden. Check your Settings to unhide it.
			</Flex>
		);
	}

	function checkScroll() {
		if (!stickToBottom) return;
		scrollToBottom();
	}

	function scrollToBottom() {
		if (!container.current) return;
		const { scrollHeight } = container.current as HTMLDivElement;
		container.current.scrollTo(0, scrollHeight);
	}

	function handleScroll() {
		if (!container.current) return;
		const { scrollHeight, scrollTop, clientHeight } = container.current;
		// <= since some browsers allow to scroll past the bottom
		// 50 gives about 3 messages of buffer with normal font size
		if (scrollHeight - scrollTop <= clientHeight + 50) {
			setStickToBottom(true);
		} else {
			setStickToBottom(false);
		}
	}

	// Simplified filter functions that don't create unnecessary copies
	function processChatMessage(message: IChatMessage | IWhisperMessage) {
		if (!shouldFilterProfanity || !message.message) return message;

		// Only create a new object if we need to modify it
		return {
			...message,
			message: filterMessage(message.message),
		};
	}

	function processSystemMessage(message: ISystemMessage): ISystemMessage {
		if (!shouldFilterProfanity || !message.message) return message;

		return {
			...message,
			message: filterMessage(message.message),
		};
	}

	return (
		<Box
			overflowY='auto'
			position='relative'
			backgroundColor='rgba(0, 0, 0, 0.404)'
			gridRow={channelKeys[activeChannel] === 'Activity' ? (isMobile ? '2 / -1' : '1 / 3') : undefined}
			className='chat-message-list'
		>
			<Box
				ref={container}
				overflowY='auto'
				paddingLeft='5px'
				fontSize={fontSize}
				lineHeight='1.3'
				onScroll={handleScroll}
				height='100%'
			>
				{processedMessages.map((message) => {
					if ('type' in message && message.type === 'settings') {
						return null;
					}
					if ('type' in message && message.type === 'system-message') {
						return (
							<MemoizedChatSystemMessage
								key={
									'chat-system-message-' +
									message.createdAt +
									message.message +
									message.localIncrementingId
								}
								message={message}
							/>
						);
					}
					return (
						<MemoizedChatMessage
							message={message}
							key={message.messageId}
							settings={activeChannelSettings}
						/>
					);
				})}
			</Box>
			{!stickToBottom && (
				<Flex
					position='absolute'
					bottom='5px'
					right='15px'
					cursor='pointer'
					onClick={scrollToBottom}
					width='20px'
					height='20px'
					alignItems='center'
					justifyContent='center'
					color='gray.400'
				>
					<FaArrowCircleDown />
				</Flex>
			)}
		</Box>
	);
};

export default ChatMessageList;
